import { useNavigate, useSearchParams } from "react-router";
import ModalSimple from "../../components/modal-simple";
import Button from "../../components/button";
import Checkbox from "../../components/checkbox";
import SBPSvg from "../../components/icons/sbp";
import DeviceSlider from "../../components/device-slider";
import AddDeviceModal from "../../components/add-device-modal";
import styles from './style.module.css';
import {
  DEFAULT_DEVICE_LIMIT,
  DEVICE_LIMITS,
  DeviceLimit,
  MONTHS_BY_SUBSCRIPTION_TYPE,
  PaymentMethod,
  SUBSCRIPTION_TYPE,
  SUBSCRIPTION_TYPE_NAME
} from "../../context/types";
import { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getTariffPrice } from "../../common/tariffs";
import classNames from "classnames";
import { useStyles } from "../../context/stylesContext";
import { DataContext } from "../../context/dataProvider";
import { pluralizeDevices } from "../../utils/pluralizeDevices";

const tariffs = [
  SUBSCRIPTION_TYPE.M1,
  SUBSCRIPTION_TYPE.M3,
  SUBSCRIPTION_TYPE.M6,
  SUBSCRIPTION_TYPE.M12
];

function SelectTariffPage() {
  const [searchParams] = useSearchParams();
  const context = useContext(DataContext);
  const userServer = context?.user?.userServer ?? null;
  const hasExistingSubscription = Boolean(userServer);
  const isTrialSubscription = userServer?.subscriptionType === SUBSCRIPTION_TYPE.TEST;
  // На пробной подписке блок кол-ва устройств всегда развёрнут со слайдером,
  // как у пользователя без подписки — а не свёрнут на предвыбранных 3 устройствах.
  const showDeviceCountCollapsed = hasExistingSubscription && !isTrialSubscription;
  const [tariff, setTariff] = useState<SUBSCRIPTION_TYPE>(SUBSCRIPTION_TYPE.M1);
  const [deviceLimit, setDeviceLimit] = useState<DeviceLimit>(DEFAULT_DEVICE_LIMIT);
  const [deviceModalOpen, setDeviceModalOpen] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [payMethod, setPayMethod] = useState<PaymentMethod | null>('sbp');
  const [buyOverlayAnimated, setBuyOverlayAnimated] = useState(false);
  const navigation = useNavigate();
  const { styles: bgStyles } = useStyles();

  useEffect(() => {
    const tariffParam = searchParams.get('t');

    if (tariffParam) {
      const validTariff = Object.values(SUBSCRIPTION_TYPE).find(
        value => value === tariffParam.toLowerCase()
      );

      if (validTariff) {
        setTariff(validTariff as SUBSCRIPTION_TYPE);
      } else {
        console.warn(`Invalid tariff parameter: ${tariffParam}`);
      }
    }

    const deviceParam = searchParams.get('d');

    if (deviceParam) {
      const parsedDevice = Number(deviceParam);
      if (DEVICE_LIMITS.includes(parsedDevice as DeviceLimit)) {
        setDeviceLimit(parsedDevice as DeviceLimit);
      } else {
        console.warn(`Invalid device parameter: ${deviceParam}`);
      }
    }
  }, [searchParams]);

  // Если подписка уже была оформлена — предвыбираем текущий тариф по устройствам
  // (пока пользователь явно не передал ?d= в ссылке).
  // pendingDeviceLimit предвыбираем только когда это осмысленная очередь:
  // уменьшение на активной или любое изменение на истёкшей. pending > deviceLimit
  // на активной — устаревшее значение, иначе нельзя «увеличить обратно» до него.
  useEffect(() => {
    if (searchParams.get('d')) return;
    if (!userServer) return;

    const billed = (userServer.deviceLimit ?? DEFAULT_DEVICE_LIMIT) as DeviceLimit;
    const pending = userServer.pendingDeviceLimit;
    const isExpired = new Date(userServer.subscriptionEndDate) <= new Date();
    const queuedPending =
      pending != null && (isExpired || pending < billed) ? pending : null;
    const preselectedDeviceLimit = queuedPending ?? billed;

    if (DEVICE_LIMITS.includes(preselectedDeviceLimit)) {
      setDeviceLimit(preselectedDeviceLimit);
    }
  }, [userServer, searchParams]);

  useEffect(() => {
    if (!buyOpen) {
      setBuyOverlayAnimated(false);
      return;
    }
    const frame = requestAnimationFrame(() => setBuyOverlayAnimated(true));
    return () => cancelAnimationFrame(frame);
  }, [buyOpen]);

  const price = getTariffPrice(tariff, deviceLimit);

  const handleClickSelectTariff = () => {
    setBuyOpen(true);
  }

  const handleBuy = () => {
    if (!payMethod) return;
    navigation(`/payment/confirmation?t=${tariff}&d=${deviceLimit}&m=${payMethod}&status=pending&newOrder=true`);
  }

  const handleClose = () => {
    setBuyOverlayAnimated(false);
    setTimeout(() => setBuyOpen(false), 320);
  };

  return (
    <div
      className={`select-tariff ${styles.page}`}
      style={
        {
          "--select-tariff-panel-height": bgStyles.sheetBottomInset ?? "0px",
        } as React.CSSProperties
      }
    >
      <div className={styles.contentContainer}>
        <div className={styles.content}>
          <h1>Выберите свои условия</h1>
          <p className={styles.subtitle}>Высокая скорость 100 мб/сек<br/>и безлимитный трафик во всех тарифах</p>
        </div>
      </div>
      <ModalSimple variant="legacy">
        <div className={styles.modalContainer}>
          <div className={styles.modalScroll}>
            <div className={styles.deviceCard}>
              <div className={styles.deviceHeader}>
                <div className={styles.deviceInfo}>
                  <div className={styles.deviceBadge}>{deviceLimit}</div>
                  <div>
                    <div className={styles.deviceTitle}>{pluralizeDevices(deviceLimit, true)}</div>
                    <div className={styles.deviceSubtitle}>в одной подписке</div>
                  </div>
                </div>
                {showDeviceCountCollapsed && (
                  <button
                    type="button"
                    className={styles.deviceChangeBtn}
                    onClick={() => setDeviceModalOpen(true)}
                  >
                    Изменить
                  </button>
                )}
              </div>
              {!showDeviceCountCollapsed && (
                <div className={styles.deviceSliderWrap}>
                  <DeviceSlider
                    options={DEVICE_LIMITS}
                    value={deviceLimit}
                    onChange={(value) => setDeviceLimit(value as DeviceLimit)}
                  />
                </div>
              )}
            </div>

            <div className={styles.tariffGrid}>
              {tariffs.map((t) => {
                const tariffPrice = getTariffPrice(t, deviceLimit);
                const months = MONTHS_BY_SUBSCRIPTION_TYPE[t];
                const basePrice = getTariffPrice(SUBSCRIPTION_TYPE.M1, deviceLimit);
                const monthlyPrice = Math.round(tariffPrice / months);
                const discount =
                  months > 1 ? Math.round((1 - tariffPrice / (basePrice * months)) * 100) : 0;

                return (
                  <button
                    key={t}
                    type="button"
                    className={classNames(styles.tariffCard, {
                      [styles.tariffCardActive]: t === tariff
                    })}
                    onClick={() => setTariff(t)}
                  >
                    <div className={styles.tariffCardHead}>
                      <span className={styles.tariffCardLabel}>{SUBSCRIPTION_TYPE_NAME[t]}</span>
                      {discount > 0 && <span className={styles.tariffCardBadge}>-{discount}%</span>}
                    </div>

                    <div>
                      <div className={styles.tariffCardPrice}>{tariffPrice} ₽</div>
                      <div className={styles.tariffCardSub}>
                        {months > 1 ? `${monthlyPrice} ₽ в месяц` : 'в месяц'}
                      </div>
                    </div>

                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.containerBtn}>
            <Button className={buyOpen ? styles.btnHidden : ''} label="Далее" onClick={handleClickSelectTariff}></Button>
          </div>
        </div>
      </ModalSimple>

      {/* Панель оплаты + backdrop в portal — на всю ширину окна (не только app-frame) */}
      {buyOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`${styles.buyOverlay} ${buyOverlayAnimated ? styles.buyOverlayOpen : ""}`}
          >
            <div className={styles.backdrop} onClick={handleClose} role="presentation" />
            <div className={styles.buySheet}>
              <div className={styles.buyPanelInner}>
                <div>
                  <h2 className={styles.buyTitle}>Оплата подписки</h2>
                  <p className={styles.buySubtitle}>
                    {SUBSCRIPTION_TYPE_NAME[tariff]} · {deviceLimit} {pluralizeDevices(deviceLimit)}
                  </p>
                </div>
                <div className={styles.buyPayMethods}>
                  <Checkbox
                    defaultChecked={payMethod === "sbp"}
                    name="pay-method"
                    label="СБП"
                    onClick={() => setPayMethod("sbp")}
                    icon={<SBPSvg />}
                  />
                  {/* <Checkbox
                defaultChecked={payMethod === 'card'}
                name="pay-method"
                label="Оплата картой"
                onClick={() => setPayMethod('card')}
                icon={<PlusSvg/>}
              /> */}
                </div>
                <Button
                  className={styles.buyBtn}
                  label={`Оплатить ${price} ₽`}
                  onClick={handleBuy}
                />
                <Button
                  label="Выбрать другую подписку"
                  variant="secondary"
                  onClick={handleClose}
                />
              </div>
            </div>
          </div>,
          document.body
        )}

      {userServer && (
        <AddDeviceModal
          open={deviceModalOpen}
          onClose={() => setDeviceModalOpen(false)}
          userServer={userServer}
        />
      )}
    </div>
  );
}

export default SelectTariffPage;
